import { Pricing } from './pricing.type';

export interface UserSubscription {
  userSubscriptionId: string;
  maxSwaps: number;
  swapsLeft: number;
  usedChargers: number;
  isHavingActiveRental: boolean;
  startAt: string;
  endAt: string;
  /**
   * Only present once the pass model ships. Legacy periods that predate
   * `user_passes` belong to no pass and leave this undefined.
   */
  passId?: string | null;
  passName?: string | null;
}

/**
 * The pass after this rental's battery comes out — `swapsLeft` and
 * `usedChargers` already include it.
 *
 * The backend only deducts the swap once the station confirms the ejection, a
 * few seconds after the rent request returns. Until then
 * `/api/subscription/current` still reports the old numbers, so the rent
 * response hands us these ready-made ones instead.
 */
export interface EjectedSubscriptionInfo {
  userSubscriptionId: string;
  usedChargers: number;
  swapsLeft: number;
  maxSwaps: number;
  startAt: string;
  endAt: string;
  state?: SubscriptionPeriodState | null;
  passId?: string | null;
  periodNumber?: number;
  isFirstPeriod?: boolean | null;
}

/**
 * Lifecycle of the hold itself (`user_passes.state`).
 *
 * The backend spells it `CANCELED` with one `L` — the RFC prose uses two.
 */
export enum UserPassState {
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  ENDED = 'ENDED',
}

/**
 * What to render for the user's subscription situation, per
 * `GET /api/subscription/current`.
 */
export enum SubscriptionStatus {
  /** Send them to the swap flow. */
  USABLE = 'USABLE',
  /**
   * The hold is alive but the next period is unpaid while the charge retries.
   * Show the pass with a payment-problem state — never an empty page.
   */
  RENEWAL_PENDING = 'RENEWAL_PENDING',
  /** They hold a usable pass, but not at this station. */
  NOT_USABLE_AT_STATION = 'NOT_USABLE_AT_STATION',
  /** No hold. Show pricing selection. */
  NONE = 'NONE',
}

export interface PassPayment {
  id: string;
  gateway: string;
  cardBrand: string | null;
  cardLast4: string | null;
}

export interface UserPassSummary {
  id: string;
  /**
   * Nullable on the wire. A pass whose pricing no longer resolves has no title
   * to show.
   */
  name?: string | null;
  /**
   * Nullable on the wire — never assume a state is present. A legacy hold can
   * come back without one.
   */
  state?: UserPassState | null;
  autoRenew: boolean;
  renewalCount: number;
  /** Omitted entirely, not sent as null, while unset. */
  canceledAt?: string | null;
  renewedAt?: string | null;
  endedAt?: string | null;
  pricing?: Pricing | null;
  currency?: string;
  /**
   * Present only where the backend wraps the pass in `WithCurrencySymbol`
   * (`/api/user-pass/detail` and `/list`); that wrapper is `@JsonUnwrapped`, so
   * the symbol arrives flattened onto the pass rather than nested.
   *
   * Absent from `/api/subscription/current`, which does not wrap — and absent
   * everywhere on deployments older than the commit that added the wrapper, so
   * always keep a fallback rather than reading it directly.
   */
  symbol?: string;
}

export interface CurrentSubscriptionResponse {
  status: SubscriptionStatus;
  /**
   * Absent, not null, when there is nothing to report — a `NONE` response is
   * literally `{"status":"NONE"}` on the wire. Also absent for a legacy period
   * that belongs to no pass: read `status` and `subscription` for those users,
   * there is nothing to put on a pass screen.
   */
  pass?: UserPassSummary | null;
  subscription?: UserSubscription | null;
}

/**
 * Lifecycle of one billing period (`user_subscriptions.state`).
 */
export enum SubscriptionPeriodState {
  /** Created but not paid for — a renewal before its charge lands. Not usable. */
  REQUESTED = 'REQUESTED',
  /** Paid, but the first battery has not been ejected yet. */
  WAITING = 'WAITING',
  ACTIVE = 'ACTIVE',
  /** Time ran out with a battery still held. */
  OVERDUE = 'OVERDUE',
  /** The first ejection failed; the period never got off the ground. */
  FAILED = 'FAILED',
  ENDED = 'ENDED',
  /** Closing so the next period can be created. */
  RENEWING = 'RENEWING',
}

/**
 * The period states that represent a live entitlement — the user may swap right
 * now. Mirrors the backend's `SubscriptionState.ongoingStates`.
 *
 * `REQUESTED` and `FAILED` are deliberately absent: no money has moved for
 * either, so neither grants anything. Reading a `REQUESTED` renewal as the
 * current cycle is what made the pass screen promise swaps the user had not
 * paid for while their card was still being retried.
 */
export const LIVE_PERIOD_STATES: SubscriptionPeriodState[] = [
  SubscriptionPeriodState.ACTIVE,
  SubscriptionPeriodState.WAITING,
  SubscriptionPeriodState.OVERDUE,
  SubscriptionPeriodState.RENEWING,
];

/**
 * The period states a pass may be cancelled from. Mirrors the backend's
 * `CANCELABLE_PERIOD_STATES`.
 *
 * Cancelling while a renewal is in flight (`REQUESTED`/`RENEWING`) or has given
 * up (`OVERDUE`/`FAILED`) would flip the pass out of renewable, so the next
 * retry skips: the renewal is never marked failed and an unreturned battery's
 * fee is never collected. The backend refuses it, so the client must not offer
 * it.
 */
export const CANCELABLE_PERIOD_STATES: SubscriptionPeriodState[] = [
  SubscriptionPeriodState.WAITING,
  SubscriptionPeriodState.ACTIVE,
];

export interface UserSubscriptionInvoice {
  preAuthAmount: number | null;
  chargedNetPayableAmount: number | null;
  subtotalAmount: number | null;
  unreturnedFeeAmount: number | null;
  totalRentalCost: number | null;
  cardPaymentAmount: number | null;
  usedAccountBalance: number | null;
  tax: number | null;
  unreturnedFeeTax: number | null;
}

export interface UserSubscriptionPeriod {
  id: string;
  name: string;
  usedChargers: number;
  maxSwaps: number;
  swapsLeft: number;
  startAt: string;
  endAt: string;
  state: SubscriptionPeriodState;
  passId: string | null;
  /**
   * `true` on the pass's original purchase, `false` on a renewal, and `null`
   * whenever the backend cannot answer.
   */
  isFirstPeriod: boolean | null;
  /**
   * Which billing period of the pass this is: 1 for the purchase, 2 for the
   * first renewal, and so on. Always 1 for a period belonging to no pass, where
   * the ordinal carries no meaning — read `isFirstPeriod` to tell those apart.
   */
  periodNumber?: number;
  currency?: string;
  payment: PassPayment | null;
  invoice: UserSubscriptionInvoice | null;
}

export interface UserPassDetail {
  pass: UserPassSummary;
  currentPeriodId: string | null;
  payment: PassPayment | null;
  /** Newest first; `currentPeriodId` is the first of them. */
  periods: UserSubscriptionPeriod[];
}

export enum UserPassAction {
  CANCEL = 'CANCEL',
  RENEW = 'RENEW',
}

export interface UpdateUserPassStatusRequest {
  passId: string;
  action: UserPassAction;
}
